/*
 * The (legacy, current) pair of every class and token a host page styles: `sanitize()` rewrites
 * the legacy name in the host's CSS before injecting it. A rename adds its pair here in the same
 * commit, or the host breaks silently. Entries are whole names, never prefixes, so that a
 * rewrite cannot reach into a longer name that is still valid.
 */

// Only a name a host actually styles goes in here.
const CLASSES = [
  ['.credere-bank', '.credere-bank-logo'],
  ['.credere-buttons', '.credere-button-group'],
  ['.credere-distribution-form-core', '.credere-distribution-schema-form'],
  ['.credere-header', '.credere-section-header'],
  ['.credere-header-subtitle', '.credere-section-header-subtitle'],
  ['.credere-header-title', '.credere-section-header-title'],
  ['.credere-hero', '.credere-callout-card'],
  ['.credere-hero-button', '.credere-callout-card-button'],
  ['.credere-hero-content', '.credere-callout-card-content'],
  ['.credere-hero-header', '.credere-callout-card-header'],
  ['.credere-hero-illustration', '.credere-callout-card-illustration'],
  ['.credere-loading', '.credere-spinner'],
  ['.credere-loading-bullet', '.credere-spinner-bullet'],
  ['.credere-schema', '.credere-form-schema-preview'],
  // The intermediate name never reached a host, so the pair points straight at the live one.
  ['.credere-simple-list', '.credere-schema-list-items'],
  ['.credere-simple-list-item', '.credere-schema-list-item'],
  ['.credere-simple-list-item-chevron', '.credere-schema-list-item-chevron'],
  ['.credere-simple-list-item-title', '.credere-schema-list-item-title'],
  ['.credere-tag', '.credere-badge'],
  ['.credere-tag-icon', '.credere-badge-icon'],
  ['.credere-tags', '.credere-badge-group'],
  ['.credere-tip', '.credere-tooltip'],
  ['.credere-tip-content', '.credere-tooltip-content'],
  ['.credere-tip-icon', '.credere-tooltip-icon'],
  ['.credere-values', '.credere-summary-list'],
  ['.credere-values-header', '.credere-summary-list-header'],
  ['.credere-values-item', '.credere-summary-list-item'],
  ['.credere-values-item-label', '.credere-summary-list-item-label'],
  ['.credere-values-item-tag', '.credere-summary-list-item-tag'],
  ['.credere-values-item-tags', '.credere-summary-list-item-tags'],
  ['.credere-values-item-value', '.credere-summary-list-item-value'],
  ['.credere-values-items', '.credere-summary-list-items'],
  ['.credere-values-section', '.credere-summary-list-section'],

  // A modifier is not `credere-` prefixed, and a host reaches it paired with the class, so a
  // class rewritten without its modifier leaves the rule pinned to nothing. One entry per letter.
  ['.variant--buttons-1a', '.variant--button-group-1a'],
  ['.variant--buttons-1b', '.variant--button-group-1b'],
  ['.variant--buttons-1c', '.variant--button-group-1c'],

  ['.color--status-01', '.color--waiting'],
  ['.color--status-02', '.color--warning'],
  ['.color--status-03', '.color--danger'],
  ['.color--status-04', '.color--success'],

  ['.color--clear', '.color--average'],
];

// A renamed `--credere-*` knob would go into a list of its own, with the `--` prefix.

/*
 * A token entry needs no leading `.` - the `--` prefix already anchors it - and one entry covers
 * both the host overriding the token and the host reading it back with `var()`. Each pair points
 * at the name production ships today, never at an intermediate one.
 */
const TOKENS = [
  ['--BASE', '--cordel-base'],
  ['--token-font-size', '--cordel-font-size'],
  ['--token-font-family-primary', '--cordel-font-family-primary'],
  ['--token-font-family-decorative', '--cordel-font-family-decorative'],
  ['--token-font-size-xs', '--cordel-font-size-xs'],
  ['--token-font-size-s', '--cordel-font-size-s'],
  ['--token-font-size-m', '--cordel-font-size-m'],
  ['--token-font-size-l', '--cordel-font-size-l'],
  ['--token-font-size-xl', '--cordel-font-size-xl'],
  ['--token-font-size-xxl', '--cordel-font-size-xxl'],
  ['--token-font-size-xxxl', '--cordel-font-size-xxxl'],
  ['--token-font-size-mega', '--cordel-font-size-mega'],
  ['--token-font-size-giga', '--cordel-font-size-giga'],
  ['--token-font-weight-regular', '--cordel-font-weight-regular'],
  ['--token-font-weight-bold', '--cordel-font-weight-bold'],
  ['--token-line-height-s', '--cordel-line-height-s'],
  ['--token-line-height-m', '--cordel-line-height-m'],
  ['--token-line-height-l', '--cordel-line-height-l'],
  ['--token-line-height-xl', '--cordel-line-height-xl'],
  ['--token-border-radius-none', '--cordel-border-radius-none'],
  ['--token-border-radius-small', '--cordel-border-radius-small'],
  ['--token-border-radius-decorative', '--cordel-border-radius-decorative'],
  ['--token-border-radius-circular', '--cordel-border-radius-circular'],
  ['--token-border-radius-pill', '--cordel-border-radius-pill'],
  ['--token-border-width-none', '--cordel-border-width-none'],
  ['--token-border-width-soft', '--cordel-border-width-soft'],
  ['--token-border-width-thin', '--cordel-border-width-thin'],
  ['--token-border-width-heavy', '--cordel-border-width-heavy'],
  ['--token-box-shadow-around-1', '--cordel-box-shadow-around-1'],
  ['--token-box-shadow-around-2', '--cordel-box-shadow-around-2'],
  ['--token-box-shadow-around-3-y', '--cordel-box-shadow-around-3-y'],
  ['--token-box-shadow-around-3', '--cordel-box-shadow-around-3'],
  ['--token-box-shadow-right-2', '--cordel-box-shadow-right-2'],
  ['--token-spacing-mini', '--cordel-spacing-mini'],
  ['--token-spacing-nano', '--cordel-spacing-nano'],
  ['--token-spacing-micro', '--cordel-spacing-micro'],
  ['--token-spacing-xxxs', '--cordel-spacing-xxxs'],
  ['--token-spacing-xxs', '--cordel-spacing-xxs'],
  ['--token-spacing-xs', '--cordel-spacing-xs'],
  ['--token-spacing-s', '--cordel-spacing-s'],
  ['--token-spacing-m', '--cordel-spacing-m'],
  ['--token-spacing-l', '--cordel-spacing-l'],
  ['--token-spacing-xl', '--cordel-spacing-xl'],
  ['--token-spacing-xxl', '--cordel-spacing-xxl'],
  ['--token-spacing-xxxl', '--cordel-spacing-xxxl'],
  ['--token-spacing-mega', '--cordel-spacing-mega'],
  ['--token-spacing-giga', '--cordel-spacing-giga'],
  ['--token-spacing-ultra', '--cordel-spacing-ultra'],
  ['--token-spacing-blaster', '--cordel-spacing-blaster'],
  ['--token-spacing-xxblaster', '--cordel-spacing-xxblaster'],
  ['--token-spacing-modal', '--cordel-spacing-modal'],
  ['--token-color-constant-darker', '--cordel-color-constant-darker'],
  ['--token-color-constant-dark', '--cordel-color-constant-dark'],
  ['--token-color-constant-light', '--cordel-color-constant-light'],
  ['--token-color-brand-primary', '--cordel-color-brand-primary'],
  ['--token-color-brand-light', '--cordel-color-brand-light'],
  ['--token-color-brand-medium', '--cordel-color-brand-medium'],
  ['--token-color-brand-dark', '--cordel-color-brand-dark'],
  ['--token-color-highlight-primary', '--cordel-color-highlight-primary'],
  ['--token-color-highlight-light', '--cordel-color-highlight-light'],
  ['--token-color-highlight-medium', '--cordel-color-highlight-medium'],
  ['--token-color-highlight-dark', '--cordel-color-highlight-dark'],
  ['--token-color-neutral-primary', '--cordel-color-neutral-primary'],
  ['--token-color-neutral-lighter', '--cordel-color-neutral-lighter'],
  ['--token-color-neutral-light', '--cordel-color-neutral-light'],
  ['--token-color-neutral-medium', '--cordel-color-neutral-medium'],
  ['--token-color-neutral-dark', '--cordel-color-neutral-dark'],
  ['--token-color-neutral-darker', '--cordel-color-neutral-darker'],
  ['--token-color-neutral-heavy', '--cordel-color-neutral-heavy'],
  ['--token-color-neutral-backdrop', '--cordel-color-neutral-backdrop'],
  ['--token-color-alert-danger', '--cordel-color-feedback-danger'],
  ['--token-color-alert-danger-dark', '--cordel-color-feedback-danger-dark'],
  ['--token-color-alert-warning', '--cordel-color-feedback-warning'],
  ['--token-color-alert-warning-dark', '--cordel-color-feedback-warning-dark'],
  ['--token-color-alert-success', '--cordel-color-feedback-success'],
  ['--token-color-alert-success-dark', '--cordel-color-feedback-success-dark'],
  ['--token-color-alert-information', '--cordel-color-feedback-information'],
  ['--token-color-alert-information-dark', '--cordel-color-feedback-information-dark'],
  ['--token-color-tag-default', '--cordel-color-badge-default'],
  ['--token-color-tag-default-average', '--cordel-color-badge-average'],
  ['--token-color-tag-default-soft', '--cordel-color-badge-soft'],
  ['--token-color-tag-bright', '--cordel-color-badge-bright'],
  ['--token-color-tag-external', '--cordel-color-badge-external'],
  ['--token-color-tag-external-heavy', '--cordel-color-badge-external-heavy'],
  ['--token-color-tag-danger', '--cordel-color-feedback-danger-soft'],
  ['--token-color-tag-danger-heavy', '--cordel-color-feedback-danger'],
  ['--token-color-tag-warning', '--cordel-color-feedback-warning-soft'],
  ['--token-color-tag-warning-heavy', '--cordel-color-feedback-warning'],
  ['--token-color-tag-success', '--cordel-color-feedback-success-soft'],
  ['--token-color-tag-success-heavy', '--cordel-color-feedback-success'],
  ['--token-color-tag-waiting', '--cordel-color-feedback-information-soft'],
  ['--token-color-tag-waiting-heavy', '--cordel-color-feedback-information'],
  ['--token-color-status-01', '--cordel-color-feedback-information'],
  ['--token-color-status-01-light', '--cordel-color-feedback-information-soft'],
  ['--token-color-status-02', '--cordel-color-feedback-warning'],
  ['--token-color-status-02-light', '--cordel-color-feedback-warning-soft'],
  ['--token-color-status-03', '--cordel-color-feedback-danger'],
  ['--token-color-status-03-light', '--cordel-color-feedback-danger-soft'],
  ['--token-color-status-04', '--cordel-color-feedback-success'],
  ['--token-color-status-04-light', '--cordel-color-feedback-success-soft'],
  ['--token-z-index-max', '--cordel-z-index-max'],
  ['--token-color-brand-contrast', '--cordel-color-brand-contrast'],
];

export const ENTRIES = CLASSES
  .concat(TOKENS)
  .sort((first, second) => second[0].length - first[0].length);

const REPLACEMENTS = ENTRIES.reduce((reduced, [previous, current]) => {
  reduced[previous] = current;

  return reduced;
}, {});

function escape(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/*
 * One alternation over every legacy name, applied in a single pass so no rewrite is rescanned.
 * The lookahead is the right-hand boundary that keeps a name out of a longer one.
 */
const PATTERN = ENTRIES.length
  ? new RegExp(`(?:${ENTRIES.map(([previous]) => escape(previous)).join('|')})(?![\\w-])`, 'g')
  : null;

export default function legacy(string = '') {
  return PATTERN ? string.replace(PATTERN, (match) => REPLACEMENTS[match]) : string;
}
